import { stripeModule } from '@/store/stripe/module'
import { isPromise } from '@/store/utils'
import type Stripe from 'stripe'
import { referralModule } from '../referral/module'
import { mobileModule } from '@/store/mobile/module'

const getStripePrices = () => {
    const stripePrices = stripeModule.state.prices.value
    if (isPromise(stripePrices)) {
        return []
    } else {
        return stripePrices
    }
}

const getStripePricesForBundle = (bundleId: string, isMobilePricesRequest?: boolean) => {
    const allPrices = getStripePrices()

    // Group prices for web or mobile (OOAP) usage
    const pricesByPlatform = allPrices.reduce((acc, price) => {
        // check lookup_key first and then fall back to price.id
        const lookupKeyOrPriceId = price.lookup_key || price.id
        const isPriceForCurrentBundle = lookupKeyOrPriceId.startsWith(`${bundleId}_`)
        const isMobilePrice = lookupKeyOrPriceId.includes('_mobile')
        if (isPriceForCurrentBundle && isMobilePrice) {
            acc.mobile.push(price)
        } else if (isPriceForCurrentBundle) {
            acc.web.push(price)
        }
        return acc
    }, {
        web: [] as Stripe.Price[],
        mobile: [] as Stripe.Price[],
    })

    // If user is in mobile out-of-app purchase flow (and if all mobile substitute prices are available),
    // use the mobile-specific stripe prices
    // For EditSubscriptionSidePanel, we want to make sure we show the users the mobile prices if they did an OOAP
    // use isMobilePricesRequest: true to make this request ofr the mobile prices
    const isOOAP = isMobilePricesRequest ?? mobileModule.getters.isInArrivedFromMobileState()
    const haveEqualPlatformPriceCount = pricesByPlatform.mobile.length === pricesByPlatform.web.length
    const bundlePricesForCurrentPlatform = isOOAP && haveEqualPlatformPriceCount
        ? pricesByPlatform.mobile
        : pricesByPlatform.web

    return bundlePricesForCurrentPlatform || []
}

const getStripePaymentMethods = () => {
    const stripePaymentMethods = stripeModule.state.payment.value
    if (isPromise(stripePaymentMethods)) {
        return []
    } else {
        const paymentMethods = stripePaymentMethods.paymentMethods

        const currentMonth = new Date().getMonth() + 1
        const currentYear = Number(
            new Date()
                .getFullYear()
                .toString()
                .slice(-2)
        )

        // filter out expired cards
        paymentMethods.filter((pm) => {
            if (pm?.card) {
                if (pm.card?.exp_year > currentYear) {
                    return pm
                }
                if (pm.card?.exp_year === currentYear && pm.card?.exp_month >= currentMonth) {
                    return pm
                }
            }
            return
        })

        return paymentMethods
    }
}

const getPriceLabel = (params: {
    p: Stripe.Price
    hidePeriodName?: boolean
    isLongLabel?: boolean
}) => {
    const { p, hidePeriodName, isLongLabel } = params

    const period = p.recurring?.interval === 'year'
        ? 'year'
        : p.recurring?.interval_count === 1
            ? 'month'
            : '3 months'
    const periodName = period === 'year'
        ? 'Yearly'
        : period === 'month'
            ? 'Monthly'
            : 'Quarterly'

    return `${
        !hidePeriodName ? `${periodName} - ` : ''
    }$${
        (p.unit_amount || 0) / 100}${isLongLabel ? ' every ' : '/'
    }${period}`
}

const getPriceSubtext = (p: Stripe.Price) => {
    const price = (referralModule.getters.calculateDiscountedPrice(p.unit_amount || 0) / 100).toFixed(2)
    return `Your first charge will be $${price}`
}

const getStripePriceOptions = (bundleId: string, hidePeriodName?: boolean, isMobilePricesRequest?: boolean) => {
    const bundlePrices = getStripePricesForBundle(bundleId, isMobilePricesRequest)
    if (!bundlePrices) {
        return []
    }

    const hasReferralDiscount = referralModule.getters.getValidReferral()

    return [ ...bundlePrices ]
        .sort((a, b) => (a.unit_amount || 0) - (b.unit_amount || 0))
        .map(p => ({
            label: getPriceLabel({ p, hidePeriodName, isLongLabel: true }),
            value: p.id,
            subtext: hasReferralDiscount ? getPriceSubtext(p) : undefined,
        }))
}

const getStripeSubscriptions = () => {
    const stripeSubscriptions = stripeModule.state.subscriptions.value
    if (isPromise(stripeSubscriptions)) {
        return []
    } else {
        return stripeSubscriptions
    }
}

const getStripe = () => {
    return stripeModule.state.stripe
}

const getElements = () => {
    return stripeModule.state.elements
}

export default {
    getStripePrices,
    getStripePricesForBundle,
    getStripeSubscriptions,
    getStripePriceOptions,
    getStripePaymentMethods,
    getPriceLabel,
    getStripe,
    getElements,
}
